export const disciplineTypes = {
    Trap: [
        'American Trap',
        'American Trap Doubles',
        'Double Barrel',
        'Double Rise',
        'Down The Line',
        'Olympic Double Trap',
        'Olympic Trap',
        'Single Barrel',
        'Wobble Trap'
    ],
    Skeet: [
        'English Skeet',
        'American Skeet',
        'Olympic Skeet',
        'Olympic Skeet Pre 2012',
        'Olympic Skeet Final',
        'Skeet Doubles',
        'Skeet Shoot Off',
        'Wobble Skeet'
    ],
    Sporting: [
        'Compak Sporting',
        'English Sporting',
        'FITASC Sporting',
        'Five Stand',
        'Sporttrap',
        'Super Sporting'
    ]
};

const buildAlphabetPreset = (count) => ({
    label: `${count} clay${count > 1 ? 's' : ''}`,
    labels: Array.from({ length: count }, (_, index) => String.fromCharCode(65 + index))
});

const PRESET_OPTIONS = {
    Sporting: [
        buildAlphabetPreset(1),
        buildAlphabetPreset(2),
        buildAlphabetPreset(3),
        buildAlphabetPreset(4),
        buildAlphabetPreset(5),
        { label: 'A to F', labels: ['A', 'B', 'C', 'D', 'E', 'F'] },
        { label: 'High / Low', labels: ['High', 'Low'] }
    ],
    Skeet: [
        { label: 'High / Low', labels: ['High', 'Low'] },
        { label: 'High / Low / Double', labels: ['High', 'Low', 'Double'] },
        { label: 'Single target', labels: ['Single'] },
        buildAlphabetPreset(2),
        buildAlphabetPreset(4)
    ],
    Trap: [
        { label: 'Single target', labels: ['Single'] },
        { label: 'Single / Double', labels: ['Single', 'Double'] },
        buildAlphabetPreset(2),
        buildAlphabetPreset(3)
    ]
};

export const COMMON_TARGET_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'High', 'Low', 'Single', 'Double', 'Rabbit', 'Teal'];
export const TARGET_TYPE_OPTIONS = [
    { value: 'single', label: 'Single' },
    { value: 'double', label: 'Double' },
    { value: 'a&e', label: 'A&E' },
    { value: 'sim_pair', label: 'Sim Pair' },
    { value: 'report_pair', label: 'Report Pair' }
];
export const DIRECTION_OPTIONS = [
    { value: '', label: 'Direction (optional)' },
    { value: 'l-r', label: 'Left to Right' },
    { value: 'r-l', label: 'Right to Left' },
    { value: 'incoming', label: 'Incoming' },
    { value: 'outgoing', label: 'Outgoing' },
    { value: 'teal', label: 'Teal' },
    { value: 'looping', label: 'Looping' },
    { value: 'rabbit', label: 'Rabbit' },
    { value: 'overhead', label: 'Overhead' }
];
export const DEFAULT_PRESENTATION = { targetType: 'single', direction: '', distance: '' };

export const normaliseTargetLabels = (labels) => {
    if (!Array.isArray(labels)) {
        return [];
    }

    return [...new Set(labels
        .map((label) => String(label || '').trim())
        .filter(Boolean))]
        .slice(0, 12);
};

export const parseTargetLabelsInput = (value) =>
    normaliseTargetLabels(
        String(value || '')
            .split(/[,\n/]+/)
            .map((part) => part.trim())
    );

export const extractTargetLabels = (clayConfigJson) => {
    if (!clayConfigJson) {
        return [];
    }

    try {
        const parsed = JSON.parse(clayConfigJson);
        const stands = Array.isArray(parsed?.stands) ? parsed.stands : Array.isArray(parsed) ? parsed : [];
        const firstStand = stands[0] || parsed || {};
        const labels = Array.isArray(firstStand?.targetLabels)
            ? firstStand.targetLabels
            : Array.isArray(firstStand?.targets)
                ? firstStand.targets.map((target, index) =>
                    typeof target === 'string' ? target : target?.label || target?.name || `T${index + 1}`
                )
                : Array.isArray(firstStand?.clays)
                    ? firstStand.clays.map((target, index) =>
                        typeof target === 'string' ? target : target?.label || target?.name || `T${index + 1}`
                    )
                    : [];

        return normaliseTargetLabels(labels);
    } catch {
        return [];
    }
};

export const getPresetOptions = (discipline) => PRESET_OPTIONS[discipline] || PRESET_OPTIONS.Sporting;

export const getDefaultTargetLabelsForDiscipline = (discipline) => getPresetOptions(discipline)[0]?.labels || ['A', 'B'];

export const buildStandType = (discipline, variant) => [discipline, variant].filter(Boolean).join(' - ');

export const parseStandIdentity = (name) => {
    const safeName = String(name || '').trim();
    if (!safeName) {
        return {
            standNumber: '',
            standTitle: '',
            displayName: ''
        };
    }

    const numberedMatch = safeName.match(/^(?:stand\s*)?(\d+)(?:\s*[-:]\s*(.+))?$/i);
    if (numberedMatch) {
        const [, standNumber, standTitle = ''] = numberedMatch;
        return {
            standNumber,
            standTitle: standTitle.trim(),
            displayName: `Stand ${standNumber}${standTitle.trim() ? ` - ${standTitle.trim()}` : ''}`
        };
    }

    return {
        standNumber: '',
        standTitle: safeName,
        displayName: safeName
    };
};

export const formatStandIdentity = (standNumber, standTitle, fallbackName = '') => {
    const safeNumber = String(standNumber || '').trim();
    const safeTitle = String(standTitle || '').trim();

    if (safeNumber) {
        return `Stand ${safeNumber}${safeTitle ? ` - ${safeTitle}` : ''}`;
    }

    return safeTitle || String(fallbackName || '').trim();
};

export const compareStandsByIdentity = (leftStand, rightStand) => {
    const leftDisplayOrder = Number(leftStand?.displayOrder || 0);
    const rightDisplayOrder = Number(rightStand?.displayOrder || 0);
    const leftHasDisplayOrder = Number.isFinite(leftDisplayOrder) && leftDisplayOrder > 0;
    const rightHasDisplayOrder = Number.isFinite(rightDisplayOrder) && rightDisplayOrder > 0;

    if (leftHasDisplayOrder && rightHasDisplayOrder && leftDisplayOrder !== rightDisplayOrder) {
        return leftDisplayOrder - rightDisplayOrder;
    }

    if (leftHasDisplayOrder && !rightHasDisplayOrder) {
        return -1;
    }

    if (!leftHasDisplayOrder && rightHasDisplayOrder) {
        return 1;
    }

    const left = parseStandIdentity(leftStand?.name);
    const right = parseStandIdentity(rightStand?.name);
    const leftNumber = Number.parseInt(left.standNumber, 10);
    const rightNumber = Number.parseInt(right.standNumber, 10);

    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftNumber !== rightNumber) {
        return leftNumber - rightNumber;
    }

    if (Number.isFinite(leftNumber) && !Number.isFinite(rightNumber)) {
        return -1;
    }

    if (!Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
        return 1;
    }

    return String(left.displayName || leftStand?.name || '').localeCompare(
        String(right.displayName || rightStand?.name || ''),
        undefined,
        { numeric: true, sensitivity: 'base' }
    );
};

export const getStandSetupStatus = (stand) => {
    const clayLabels = extractTargetLabels(stand?.clayConfigJson);
    return clayLabels.length > 0 ? 'configured' : 'needs-clay-setup';
};

export const buildPresentationDefaults = (targetLabels, existingDefaults = {}) => {
    return normaliseTargetLabels(targetLabels).reduce((accumulator, label) => {
        const currentDefaults = existingDefaults?.[label] || {};
        accumulator[label] = {
            targetType: currentDefaults.targetType || DEFAULT_PRESENTATION.targetType,
            direction: currentDefaults.direction || DEFAULT_PRESENTATION.direction,
            distance: currentDefaults.distance || DEFAULT_PRESENTATION.distance
        };

        return accumulator;
    }, {});
};

export const extractPresentationDefaults = (clayConfigJson) => {
    if (!clayConfigJson) {
        return {};
    }

    try {
        const parsed = JSON.parse(clayConfigJson);
        const stands = Array.isArray(parsed?.stands) ? parsed.stands : Array.isArray(parsed) ? parsed : [];
        const firstStand = stands[0] || parsed || {};
        const presentations = Array.isArray(firstStand?.presentations) ? firstStand.presentations : [];

        return presentations.reduce((accumulator, presentation) => {
            const label = String(presentation?.label || '').trim();
            if (!label || accumulator[label]) {
                return accumulator;
            }

            accumulator[label] = {
                targetType: presentation?.targetType || DEFAULT_PRESENTATION.targetType,
                direction: presentation?.direction || DEFAULT_PRESENTATION.direction,
                distance: presentation?.distance != null ? String(presentation.distance) : DEFAULT_PRESENTATION.distance
            };

            return accumulator;
        }, {});
    } catch {
        return {};
    }
};

export const buildStandClayConfig = ({ standName, discipline, variant, targetLabels, presentationDefaults = {} }) => {
    const safeTargetLabels = normaliseTargetLabels(targetLabels);
    const safeDefaults = buildPresentationDefaults(safeTargetLabels, presentationDefaults);
    const presentations = safeTargetLabels.map((label) => ({
        label,
        targetType: safeDefaults[label]?.targetType || DEFAULT_PRESENTATION.targetType,
        direction: safeDefaults[label]?.direction || DEFAULT_PRESENTATION.direction,
        distance: safeDefaults[label]?.distance === '' ? null : Number(safeDefaults[label]?.distance)
    }));

    return JSON.stringify({
        discipline: discipline || null,
        variant: variant || null,
        stands: [
            {
                standNumber: 1,
                standName: standName || '',
                targetLabels: safeTargetLabels,
                presentations
            }
        ]
    });
};
